import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ArrowRight, MessageSquare, Shapes, LifeBuoy, TrendingUp, Lock } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WelcomeMascot } from "@/components/Mascot";
import { ProgressSection } from "@/pages/ProgressPage";
import { ActivityHeatmap } from "@/components/ActivityHeatmap";
import { HomeComposer } from "@/components/HomeComposer";
import { ExamCountdown } from "@/components/ExamCountdown";
import { ArtifactIcon } from "@/pages/ArtifactsPage";
import { HELP_ARTICLES } from "@/lib/helpArticles";
import { logWarn } from "@/lib/log";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Card({ title, icon: Icon, link, linkLabel, testid, children }) {
  return (
    <section className="soft-card p-5 sm:p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-medium flex items-center gap-2"><Icon className="h-4 w-4 text-sky-300" />{title}</h3>
        {link && <Link to={link} className="text-xs text-sky-400 hover:underline" data-testid={`${testid}-all`}>{linkLabel}</Link>}
      </div>
      {children}
    </section>
  );
}

const Empty = ({ children, testid }) => <p className="text-sm text-neutral-500 py-3" data-testid={testid}>{children}</p>;

function RecentChats({ chats }) {
  return (
    <Card title="Recent chats" icon={MessageSquare} link="/app/chats" linkLabel="All chats" testid="home-recent-chats">
      {!chats.length && <Empty testid="home-recent-chats-empty">No chats yet. Ask Bryn something below to start your first one.</Empty>}
      <ul className="-mx-2">
        {chats.slice(0, 5).map((c) => (
          <li key={c.chat_id}>
            <Link to={`/app/c/${c.chat_id}`} data-testid={`home-chat-${c.chat_id}`} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.05] transition-colors">
              <span className="flex-1 min-w-0 truncate text-sm text-neutral-200">{c.title}</span>
              <span className="text-xs text-neutral-500 shrink-0">{day(c.updated_at)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RecentArtifacts() {
  const [items, setItems] = useState(null);
  useEffect(() => { http.get("/artifacts").then((r) => setItems(r.data)).catch((e) => { logWarn("Artifacts failed", e); setItems([]); }); }, []);
  return (
    <Card title="Recent artifacts" icon={Shapes} link="/app/artifacts" linkLabel="All artifacts" testid="home-recent-artifacts">
      {items && !items.length && <Empty testid="home-recent-artifacts-empty">Documents, study plans, flashcards, practice sets and images Bryn makes for you will collect here.</Empty>}
      {!items && <div className="skeleton h-24" />}
      <ul className="-mx-2">
        {(items || []).slice(0, 5).map((a) => (
          <li key={a.id}>
            <Link to={`/app/artifacts?open=${encodeURIComponent(a.id)}`} data-testid={`home-artifact-${a.id}`} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.05] transition-colors">
              <ArtifactIcon kind={a.kind} />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm text-neutral-200">{a.title}</span>
                <span className="block text-xs text-neutral-500">{a.type}</span>
              </span>
              <span className="text-xs text-neutral-500 shrink-0">{day(a.created_at)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Help() {
  return (
    <Card title="Help and resources" icon={LifeBuoy} link="/app/help" linkLabel="Help center" testid="home-help">
      <ul className="grid sm:grid-cols-2 gap-x-6 -mx-2">
        {HELP_ARTICLES.slice(0, 4).map((a) => (
          <li key={a.slug}>
            <Link to={`/app/help/${a.slug}`} data-testid={`home-help-${a.slug}`} className="block px-2 py-2 rounded-lg hover:bg-white/[0.05] transition-colors">
              <span className="block text-sm text-neutral-200">{a.title}</span>
              <span className="block text-xs text-neutral-500 line-clamp-1">{a.summary}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Progress({ ent, lastChat }) {
  const navigate = useNavigate();
  return (
    <section data-testid="home-progress">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-300" />Learning progress</h2>
        {lastChat && (
          <button onClick={() => navigate(`/app/c/${lastChat.chat_id}`)} data-testid="home-continue-btn"
            className="text-sm inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/15 hover:bg-white/5 max-w-full">
            <span className="truncate">Continue: {lastChat.title}</span><ArrowRight className="h-3.5 w-3.5 shrink-0" />
          </button>
        )}
      </div>
      {ent?.features.progress ? <ProgressSection /> : (
        <div className="soft-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="home-progress-locked">
          <p className="text-sm text-neutral-300 flex items-center gap-2"><Lock className="h-4 w-4 text-neutral-500" />Topic mastery, practice accuracy and recurring mistakes are on Starter and above.</p>
          <Link to="/app/billing" className="text-xs px-3 py-1.5 rounded-full border border-sky-300/30 text-sky-300 hover:bg-sky-500/10" data-testid="home-progress-upgrade">See plans</Link>
        </div>
      )}
    </section>
  );
}

export default function HomePage() {
  const { user, ent } = useAuth();
  const { chats } = useOutletContext();
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto scroll-thin" data-testid="home-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-10 space-y-8">
          <header className="flex items-center gap-4 sm:gap-6">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-sky-300 mb-1 rise">Hi {user?.name?.split(" ")[0]}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight rise" style={{ animationDelay: "40ms" }}>Ready to study?</h1>
              <p className="text-neutral-400 mt-2 text-sm sm:text-base rise" style={{ animationDelay: "80ms" }}>Pick up where you left off, or ask Bryn something new below.</p>
            </div>
            <WelcomeMascot className="h-24 w-24 sm:h-32 sm:w-32 shrink-0 drop-shadow-[0_14px_30px_rgba(37,99,255,0.35)]" />
          </header>
          <ExamCountdown />
          <Progress ent={ent} lastChat={chats[0]} />
          <div className="grid lg:grid-cols-2 gap-6">
            <RecentChats chats={chats} />
            <RecentArtifacts />
          </div>
          <ActivityHeatmap />
          <Help />
        </div>
      </div>
      <HomeComposer />
    </div>
  );
}
